import { action, computed, observable } from 'mobx';
import { computedFn } from 'mobx-utils';

import { badgeStoreStatuses, BadgeStoreStatuses } from '@/constants/badges';
import { query } from '@/constants/query';
import { serviceTypes } from '@/constants/services';
import { SettingsResponse, SyncPlayerBadge } from '@/types/api';
import {
  Badge,
  BadgeConditions,
  Badges,
  StoreBadge,
  UserBadge,
  UserBadges,
} from '@/types/models/badge';
import { mapStoreBadges } from '@/utils/mapSettings';
import { ServicesMixin } from '@/utils/servicesMixin';

import QueryStore from './QueryStore';

@ServicesMixin([serviceTypes.QUERY])
export default class BadgeStore {
  query: QueryStore;

  @observable public accessor dialogBadgeMinted = false;

  @observable public accessor selectedBadgeId: number | null = null;

  @observable public accessor userBadges: UserBadges = {};
  @observable public accessor badges: Badges = {};

  @computed public get selectedBadge() {
    if (!this.selectedBadgeId) return null;

    return this.badges[this.selectedBadgeId];
  }

  @computed public get badgesCount() {
    return Object.values(this.userBadges).reduce<number>(
      (sum, badge) => sum + badge.count,
      0,
    );
  }

  constructor(services?: [QueryStore]) {
    const [queryService] = services!;
    this.query = queryService;
  }

  getBadgeProcessing = computedFn(
    () => !!this.query.get(query.storeBadge).state.pending,
  );

  getUserBadge = computedFn(
    (externalId: number): UserBadge =>
      this.userBadges[externalId] || {
        externalId,
        count: 0,
      },
  );

  private getStoreBadgeStatus = computedFn(
    ({
      conditions,
    }: {
      badge: Badge;
      conditions: BadgeConditions;
    }): BadgeStoreStatuses => {
      if (conditions.keys.available) {
        return badgeStoreStatuses.available;
      }

      return badgeStoreStatuses.locked;
    },
  );

  getStoreBadge = computedFn(
    (options: {
      externalId: number;
      userBadge: UserBadge;
      conditions: BadgeConditions;
    }): StoreBadge => {
      const badge = this.badges[options.externalId];

      return {
        externalId: options.externalId,
        badge,
        userBadge: options.userBadge,
        status: this.getStoreBadgeStatus({
          badge,
          conditions: options.conditions,
        }),
        processing: this.getBadgeProcessing(),
        сonditions: options.conditions,
      };
    },
  );

  @action.bound public init({
    settings,
    userBadges,
  }: {
    settings: SettingsResponse;
    userBadges: SyncPlayerBadge[];
  }) {
    this.setSettings(settings);
    this.setUserBadges(userBadges);
  }

  @action.bound public setUserBadges(list: SyncPlayerBadge[] = []) {
    this.userBadges = list.reduce<Record<number, UserBadge>>(
      (badges, badge) => ({
        ...badges,
        [badge.external_id]: {
          count: badge.count || 0,
          externalId: badge.external_id,
        },
      }),
      {},
    );
  }

  @action.bound public setUserBadge(badge: SyncPlayerBadge) {
    this.userBadges[badge.external_id] = {
      count: badge.count || 0,
      externalId: badge.external_id,
    };
  }

  @action.bound public setSettings = (settings: SettingsResponse) => {
    this.badges = mapStoreBadges(settings.badge);
  };

  @action.bound public setDialogBadgeMinted(value: boolean) {
    this.dialogBadgeMinted = value;
  }

  @action.bound public selectBadge = (badge: Badge | null) => {
    if (!badge) return;
    this.selectedBadgeId = badge.externalId;
  };
}
